import React, { useState, useEffect } from 'react';
import { ArrowUpIcon, ArrowDownIcon, ChartBarIcon, MusicalNoteIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { subDays } from 'date-fns';

const MonitorOverview = ({ sessions }) => {
  const [overviewData, setOverviewData] = useState({
    exercisesPlayedLast7Days: 0,
    favoriteExercise: { name: 'None', count: 0 },
    volumeBalance: { last7: 0, last30: 0, diff: 0, direction: 'neutral' },
    synchronization: { last7: 0, last30: 0, diff: 0, direction: 'neutral' },
    articulationAccuracy: { last7: 0, last30: 0, diff: 0, direction: 'neutral' },
    f1Score: { last7: 0, last30: 0, diff: 0, direction: 'neutral' }
  });

  useEffect(() => {
    if (!sessions || sessions.length === 0) {
      return;
    }

    calculateOverviewMetrics(sessions);
  }, [sessions]);

  const calculateOverviewMetrics = (allSessions) => {
    const now = new Date();
    const sevenDaysAgo = subDays(now, 7);
    const thirtyDaysAgo = subDays(now, 30);

    // Filter sessions by date range
    const last7Sessions = allSessions.filter(s => 
      new Date(s.start_timestamp) >= sevenDaysAgo
    );
    
    const last30Sessions = allSessions.filter(s => 
      new Date(s.start_timestamp) >= thirtyDaysAgo
    );

    // 1. Exercises played in last 7 days
    const exercisesPlayedLast7Days = last7Sessions.length;

    // 2. Favorite exercise last 7 days (most played)
    const exerciseCounts = {};
    last7Sessions.forEach(session => {
      const name = session.exercise_name || 'Unknown';
      exerciseCounts[name] = (exerciseCounts[name] || 0) + 1;
    });
    
    let favoriteExercise = { name: 'None', count: 0 };
    Object.entries(exerciseCounts).forEach(([name, count]) => {
      if (count > favoriteExercise.count) {
        favoriteExercise = { name, count };
      }
    });

    // 3. Volume balance (average absolute volume delta across all fingers)
    const volumeBalanceLast7 = calculateAvgVolumeDelta(last7Sessions);
    const volumeBalanceLast30 = calculateAvgVolumeDelta(last30Sessions);
    const volumeDiff = volumeBalanceLast7 - volumeBalanceLast30;
    const volumeDirection = volumeDiff > 0.5 ? 'up' : volumeDiff < -0.5 ? 'down' : 'neutral';

    // 4. Synchronization (average synchronization across all fingers)
    const synchronizationLast7 = calculateAvgSynchronization(last7Sessions);
    const synchronizationLast30 = calculateAvgSynchronization(last30Sessions);
    const syncDiff = synchronizationLast7 - synchronizationLast30;
    const syncDirection = syncDiff > 0.5 ? 'up' : syncDiff < -0.5 ? 'down' : 'neutral';

    // 5. Articulation accuracy (average articulation/precision across all fingers)
    const articulationLast7 = calculateAvgArticulation(last7Sessions);
    const articulationLast30 = calculateAvgArticulation(last30Sessions);
    const articDiff = articulationLast7 - articulationLast30;
    const articDirection = articDiff > 0.5 ? 'up' : articDiff < -0.5 ? 'down' : 'neutral';

    // 6. F1 Score (harmonic mean of precision and recall)
    const f1ScoreLast7 = calculateAvgF1Score(last7Sessions);
    const f1ScoreLast30 = calculateAvgF1Score(last30Sessions);
    const f1Diff = f1ScoreLast7 - f1ScoreLast30;
    const f1Direction = f1Diff > 0.5 ? 'up' : f1Diff < -0.5 ? 'down' : 'neutral';

    setOverviewData({
      exercisesPlayedLast7Days,
      favoriteExercise,
      volumeBalance: { 
        last7: volumeBalanceLast7, 
        last30: volumeBalanceLast30, 
        diff: volumeDiff, 
        direction: volumeDirection 
      },
      synchronization: { 
        last7: synchronizationLast7, 
        last30: synchronizationLast30, 
        diff: syncDiff, 
        direction: syncDirection 
      },
      articulationAccuracy: { 
        last7: articulationLast7, 
        last30: articulationLast30, 
        diff: articDiff, 
        direction: articDirection 
      },
      f1Score: { 
        last7: f1ScoreLast7, 
        last30: f1ScoreLast30, 
        diff: f1Diff, 
        direction: f1Direction 
      }
    });
  };

  const calculateAvgVolumeDelta = (sessionList) => {
    if (!sessionList || sessionList.length === 0) return 0;
    
    // Get session-level balance (positive = RH louder, negative = LH louder)
    const balanceValues = [];
    sessionList.forEach(session => {
      // Use the balance field from backend (right_avg_velocity - left_avg_velocity)
      if (session.balance !== undefined && session.balance !== null) {
        balanceValues.push(session.balance);
      }
    });
    
    return balanceValues.length > 0 ? balanceValues.reduce((a, b) => a + b, 0) / balanceValues.length : 0;
  };

  const calculateAvgSynchronization = (sessionList) => {
    if (!sessionList || sessionList.length === 0) return 0;
    
    // Get session-level synchronization (positive = RH later, negative = LH later)
    const syncValues = [];
    sessionList.forEach(session => {
      // Use the synchronization field from backend (right_avg_lateness - left_avg_lateness)
      if (session.synchronization !== undefined && session.synchronization !== null) {
        syncValues.push(session.synchronization);
      }
    });
    
    return syncValues.length > 0 ? syncValues.reduce((a, b) => a + b, 0) / syncValues.length : 0;
  };

  const calculateAvgArticulation = (sessionList) => {
    if (!sessionList || sessionList.length === 0) return 0;
    
    // Get session-level articulation/precision metrics
    const articValues = [];
    sessionList.forEach(session => {
      let articValue = null;
      
      // Check for aggregate precision field
      if (session.precision !== undefined && session.precision !== null) {
        articValue = session.precision * 100; // Convert to percentage
      }
      // Or check for f1_score
      else if (session.f1_score !== undefined && session.f1_score !== null) {
        articValue = session.f1_score * 100; // Convert to percentage
      }
      // Or calculate from correct/total notes
      else if (session.correct_notes !== undefined && session.total_notes !== undefined &&
               session.total_notes > 0) {
        articValue = (session.correct_notes / session.total_notes) * 100;
      }
      
      // Only add non-null values
      if (articValue !== null && !isNaN(articValue)) {
        articValues.push(articValue);
      }
    });
    
    return articValues.length > 0 ? articValues.reduce((a, b) => a + b, 0) / articValues.length : 0;
  };

  const calculateAvgF1Score = (sessionList) => {
    if (!sessionList || sessionList.length === 0) return 0;
    
    // Get session-level F1 score
    const f1Values = [];
    sessionList.forEach(session => {
      let f1Value = null;
      
      // Check if session has f1_score field (aggregate level)
      if (session.f1_score !== undefined && session.f1_score !== null) {
        f1Value = session.f1_score * 100; // Convert to percentage
      } 
      // Calculate from precision and recall if available
      else if (session.precision !== undefined && session.recall !== undefined &&
               session.precision !== null && session.recall !== null) {
        const precision = session.precision;
        const recall = session.recall;
        if ((precision + recall) > 0) {
          f1Value = (2 * precision * recall) / (precision + recall) * 100;
        }
      }
      // Or calculate from correct/total/missed notes
      else if (session.correct_notes !== undefined && session.total_notes !== undefined &&
               session.missed_notes !== undefined && session.total_notes > 0) {
        const precision = session.correct_notes / (session.correct_notes + (session.erroneous_notes || 0));
        const recall = session.correct_notes / session.total_notes;
        if ((precision + recall) > 0) {
          f1Value = (2 * precision * recall) / (precision + recall) * 100;
        }
      }
      
      // Only add non-null values
      if (f1Value !== null && !isNaN(f1Value)) {
        f1Values.push(f1Value);
      }
    });
    
    return f1Values.length > 0 ? f1Values.reduce((a, b) => a + b, 0) / f1Values.length : 0;
  };

  const getExerciseDisplayName = (name) => {
    if (!name || name === 'None') return name;
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderMetricCard = (title, metric, isArticulation = false, isBalance = false, isSync = false) => {
    const { last7, diff, direction } = metric;
    
    // For balance, format differently to show which hand is louder
    if (isBalance) {
      const absBalance = Math.abs(last7);
      const hand = last7 > 0 ? 'RH' : last7 < 0 ? 'LH' : 'Even';
      const balanceText = absBalance < 0.5 ? 'Even' : `${hand} +${absBalance.toFixed(1)}`;
      
      // For balance, getting closer to 0 is better (more even)
      // So if absolute value decreased, that's good (green down arrow)
      let arrowColor = 'text-gray-400';
      if (direction !== 'neutral') {
        // Compare absolute values: if |last7| < |last30|, that's improvement (down arrow should be green)
        const isImproving = Math.abs(last7) < Math.abs(last7 - diff); // last30 = last7 - diff
        arrowColor = isImproving ? 'text-green-600' : 'text-red-600';
      }
      
      return (
        <div className="card p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2">{title}</h4>
          <div className="flex items-center justify-between">
            <div className="text-xl font-bold text-gray-900">
              {balanceText}
            </div>
            {direction !== 'neutral' && (
              <div className={`flex items-center ${arrowColor}`}>
                {direction === 'up' ? (
                  <ArrowUpIcon className="h-5 w-5" />
                ) : (
                  <ArrowDownIcon className="h-5 w-5" />
                )}
                <span className="ml-1 text-sm font-semibold">
                  {Math.abs(diff).toFixed(1)}
                </span>
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Last 7 reps vs. 30-day avg
          </div>
        </div>
      );
    }
    
    // For synchronization, format to show which hand is later
    if (isSync) {
      const absSync = Math.abs(last7);
      const hand = last7 > 0 ? 'RH' : last7 < 0 ? 'LH' : 'Perfect';
      const syncText = absSync < 1 ? 'Perfect' : `${hand} +${absSync.toFixed(1)}ms`;
      
      // For sync, getting closer to 0 is better (more synchronized)
      let arrowColor = 'text-gray-400';
      if (direction !== 'neutral') {
        const isImproving = Math.abs(last7) < Math.abs(last7 - diff);
        arrowColor = isImproving ? 'text-green-600' : 'text-red-600';
      }
      
      return (
        <div className="card p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2">{title}</h4>
          <div className="flex items-center justify-between">
            <div className="text-xl font-bold text-gray-900">
              {syncText}
            </div>
            {direction !== 'neutral' && (
              <div className={`flex items-center ${arrowColor}`}>
                {direction === 'up' ? (
                  <ArrowUpIcon className="h-5 w-5" />
                ) : (
                  <ArrowDownIcon className="h-5 w-5" />
                )}
                <span className="ml-1 text-sm font-semibold">
                  {Math.abs(diff).toFixed(1)}
                </span>
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Last 7 reps vs. 30-day avg
          </div>
        </div>
      );
    }
    
    // Determine arrow color based on metric type
    let arrowColor = 'text-gray-400';
    if (direction !== 'neutral') {
      if (isArticulation) {
        // For articulation, higher is better
        arrowColor = direction === 'up' ? 'text-green-600' : 'text-red-600';
      } else {
        // For synchronization, lower is better (closer to 0 = more synchronized)
        arrowColor = direction === 'down' ? 'text-green-600' : 'text-red-600';
      }
    }

    return (
      <div className="card p-4">
        <h4 className="text-sm font-medium text-gray-600 mb-2">{title}</h4>
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold text-gray-900">
            {isArticulation ? `${last7.toFixed(1)}%` : last7.toFixed(1)}
          </div>
          {direction !== 'neutral' && (
            <div className={`flex items-center ${arrowColor}`}>
              {direction === 'up' ? (
                <ArrowUpIcon className="h-5 w-5" />
              ) : (
                <ArrowDownIcon className="h-5 w-5" />
              )}
              <span className="ml-1 text-sm font-semibold">
                {Math.abs(diff).toFixed(1)}
              </span>
            </div>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Last 7 reps vs. 30-day avg
        </div>
      </div>
    );
  };

  return (
    <div className="monitor-overview mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Practice Overview</h2>
      
      <div className="grid grid-cols-6 gap-4">
        {/* Exercises Played Last 7 Days */}
        <div className="card p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center">
            <ChartBarIcon className="h-4 w-4 mr-1" />
            Exercises Played
          </h4>
          <div className="text-2xl font-bold text-gray-900">
            {overviewData.exercisesPlayedLast7Days}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Last 7 days
          </div>
        </div>

        {/* Favorite Exercise */}
        <div className="card p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center">
            <TrophyIcon className="h-4 w-4 mr-1" />
            Most Played
          </h4>
          <div className="text-sm font-bold text-gray-900 truncate" title={getExerciseDisplayName(overviewData.favoriteExercise.name)}>
            {getExerciseDisplayName(overviewData.favoriteExercise.name)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {overviewData.favoriteExercise.count}x in last 7 days
          </div>
        </div>

        {/* Volume Balance */}
        {renderMetricCard('Volume Balance', overviewData.volumeBalance, false, true)}

        {/* Synchronization */}
        {renderMetricCard('Synchronization', overviewData.synchronization, false, false, true)}

        {/* Articulation Accuracy */}
        {renderMetricCard('Articulation Accuracy', overviewData.articulationAccuracy, true)}

        {/* F1 Score */}
        {renderMetricCard('F1 Score', overviewData.f1Score, true)}
      </div>
    </div>
  );
};

export default MonitorOverview;

